import { z } from 'zod'
import { baseEntitySchema } from '../entity.js'
import { colorNames } from '../../lib/color.js'
import { profileGearSchema } from '../../extensions/gear/requirement.js'

const GB = 1024 * 1024 * 1024
const storageQuota = 1 * GB
const aiTokensQuota = 100000
/**
 * Cover-song imports allowed per user. Provisional — a single import runs a research agent with
 * paid web search at roughly $0.50–$1.00, and Anthropic's per-search charge is not a token cost so
 * it is invisible to the aiTokens quota. Revisit once real per-generation cost is observed.
 */
const coverSongImportsQuota = 20

export const profileSchema = baseEntitySchema.extend({
  id: z.string(),
  type: z.literal('profile'),
  userId: z.string(),
  email: z.string(),
  name: z.string().optional(),
  /** URL of a picture for the user. Child images does take precedence, and is handled by the profiles API. */
  picture: z.union([z.string(), z.null()]).optional(),
  /** Profile color */
  color: z.enum(colorNames).optional(),

  /** Subscriber ID */
  sub: z.string().optional(),

  /**
   * User's current usage for the limits we enforce
   * @important We do DynamoDB atomicMath on these fields (not type checked) so careful with changing them
   */
  usage: z
    .object({
      storage: z.number().default(0),
      aiTokens: z.number().default(0),
      coverSongImports: z.number().default(0),
    })
    .optional()
    .default({
      storage: 0,
      aiTokens: 0,
      coverSongImports: 0,
    }),
  /** User's quota */

  quota: z
    .object({
      // The maximum storage the user can use
      storage: z.number().default(storageQuota),
      // The maximum number of tokens the user can use
      aiTokens: z.number().default(aiTokensQuota),
      // The maximum number of cover songs the user can import
      coverSongImports: z.number().default(coverSongImportsQuota),
    })
    .default({
      storage: storageQuota,
      aiTokens: aiTokensQuota,
      coverSongImports: coverSongImportsQuota,
    }),

  state: z
    .object({
      // The user has an initialized account, e.g., with starter content
      initialized: z.boolean().default(false),
    })
    .optional()
    .default({ initialized: false }),

  /**
   * User configuration. useUserConfig() should be used to access this.
   */
  config: z.record(z.string(), z.unknown()).optional().default({}),
})

export type Profile = z.infer<typeof profileSchema>

export function isProfile(node: unknown): node is Profile {
  return profileSchema.safeParse(node).success
}

const publicProfileFields = profileSchema
  .pick({
    id: true,
    type: true,
    userId: true,
    name: true,
    email: true,
    picture: true,
    color: true,
  })
  .extend({
    /** Projected from config.gear — bandmates need this to build technical riders */
    gear: profileGearSchema.optional(),
  })

/**
 * The profile shape returned for users other than the caller.
 *
 * @important The preprocess step lifts `config.gear` to a top-level `gear` field and drops the rest of
 * `config`. `config` is an open record, so it must never be exposed wholesale — add fields here explicitly.
 */
export const publicProfileSchema = z.preprocess(val => {
  const { config, ...rest } = (val ?? {}) as { config?: Record<string, unknown> }
  return { ...rest, gear: config?.gear }
}, publicProfileFields)

export type PublicProfile = z.infer<typeof publicProfileSchema>

export default profileSchema
